import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { lessonApi, progressApi } from '../../api/client';
import { ExplanationPanel } from './ExplanationPanel';
import { ExampleRunner } from './ExampleRunner';
import { TaskPanel } from './TaskPanel';
import { XpAnimation } from '../common/XpAnimation';
import { ConfettiAnimation } from '../common/ConfettiAnimation';
import { AchievementPopup } from '../Progress/AchievementPopup';
import styles from '../../styles/LessonView.module.css';
export function LessonView() {
    const { lessonId } = useParams();
    const navigate = useNavigate();
    const [lesson, setLesson] = useState(null);
    const [phase, setPhase] = useState('explanation');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [attempts, setAttempts] = useState(0);
    const [hintsRevealed, setHintsRevealed] = useState(0);
    const [hints, setHints] = useState([]);
    const [completed, setCompleted] = useState(false);
    const [completionResult, setCompletionResult] = useState(null);
    const [xpAnimTrigger, setXpAnimTrigger] = useState(0);
    const [xpAnimAmount, setXpAnimAmount] = useState(0);
    const [confettiTrigger, setConfettiTrigger] = useState(0);
    const [newAchievements, setNewAchievements] = useState([]);
    const [startedAt] = useState(Date.now());
    useEffect(() => {
        if (!lessonId)
            return;
        let cancelled = false;
        (async () => {
            setLoading(true);
            setError(null);
            setPhase('explanation');
            setAttempts(0);
            setHintsRevealed(0);
            setHints([]);
            setCompleted(false);
            setCompletionResult(null);
            try {
                const data = await lessonApi.getLesson(lessonId);
                if (!cancelled) {
                    setLesson(data);
                    setLoading(false);
                }
            }
            catch (e) {
                if (!cancelled) {
                    setError(e instanceof Error ? e.message : String(e));
                    setLoading(false);
                }
            }
        })();
        return () => { cancelled = true; };
    }, [lessonId]);
    const handleSetPhase = useCallback((p) => {
        setPhase(p);
    }, []);
    const handleRunExample = useCallback(async () => {
        if (!lessonId)
            return { success: false, error: 'No lesson', executionTime: 0 };
        try {
            return await lessonApi.runExample(lessonId);
        }
        catch (e) {
            return { success: false, error: e instanceof Error ? e.message : String(e), executionTime: 0 };
        }
    }, [lessonId]);
    const handleCheck = useCallback(async (sql) => {
        if (!lessonId)
            return { correct: false, feedback: 'No lesson' };
        try {
            const result = await lessonApi.checkAnswer(lessonId, sql);
            setAttempts((a) => a + 1);
            if (result.correct && !completed) {
                setCompleted(true);
                const timeSeconds = Math.round((Date.now() - startedAt) / 1000);
                try {
                    const completion = await progressApi.completeLesson(lessonId, attempts + 1, hintsRevealed, timeSeconds);
                    if (!completion.alreadyCompleted) {
                        setCompletionResult(completion.xp);
                        setXpAnimAmount(completion.xp.xpEarned);
                        setXpAnimTrigger((t) => t + 1);
                        if (completion.xp.leveledUp) {
                            setConfettiTrigger((t) => t + 1);
                        }
                        if (completion.newAchievements?.length) {
                            setNewAchievements(completion.newAchievements);
                        }
                    }
                }
                catch {
                    // Progress save failed but lesson check was correct
                }
            }
            return result;
        }
        catch (e) {
            return { correct: false, feedback: e instanceof Error ? e.message : String(e) };
        }
    }, [lessonId, completed, attempts, hintsRevealed, startedAt]);
    const handleRevealHint = useCallback(async () => {
        if (!lessonId)
            return;
        try {
            const result = await lessonApi.getHint(lessonId, hintsRevealed);
            if (result.hint) {
                setHints((prev) => [...prev, result.hint]);
                setHintsRevealed(result.hintsRevealed);
            }
        }
        catch {
            // hint fetch failed
        }
    }, [lessonId, hintsRevealed]);
    if (loading) {
        return _jsx("div", { className: styles.loading, children: "\u0417\u0430\u0433\u0440\u0443\u0437\u043A\u0430 \u0443\u0440\u043E\u043A\u0430..." });
    }
    if (error || !lesson) {
        return (_jsxs("div", { className: styles.container, children: [_jsx(Link, { to: "/", className: styles.backLink, children: "\u2190 \u041D\u0430\u0437\u0430\u0434 \u043A \u043A\u0430\u0440\u0442\u0435" }), _jsx("div", { className: styles.errorMessage, children: error || 'Урок не найден' })] }));
    }
    return (_jsxs(_Fragment, { children: [_jsxs("div", { className: styles.container, children: [_jsx(Link, { to: "/", className: styles.backLink, children: "\u2190 \u041D\u0430\u0437\u0430\u0434 \u043A \u043A\u0430\u0440\u0442\u0435" }), _jsxs("div", { className: styles.lessonHeader, children: [_jsxs("div", { className: styles.lessonTitle, children: ["\u0423\u0440\u043E\u043A ", lesson.id, ": ", lesson.title] }), _jsxs("div", { className: styles.phaseNav, children: [_jsx("button", { className: phase === 'explanation' ? styles.phaseBtnActive : styles.phaseBtn, onClick: () => handleSetPhase('explanation'), children: "\u041E\u0431\u044A\u044F\u0441\u043D\u0435\u043D\u0438\u0435" }), _jsx("button", { className: phase === 'example' ? styles.phaseBtnActive : styles.phaseBtn, onClick: () => handleSetPhase('example'), children: "\u041F\u0440\u0438\u043C\u0435\u0440" }), _jsx("button", { className: phase === 'task' ? styles.phaseBtnActive : styles.phaseBtn, onClick: () => handleSetPhase('task'), children: "\u0417\u0430\u0434\u0430\u043D\u0438\u0435" })] })] }), phase === 'explanation' && (_jsxs(_Fragment, { children: [_jsx(ExplanationPanel, { text: lesson.explanation.text, analogy: lesson.explanation.analogy }), _jsx("div", { className: styles.lessonNav, children: _jsx("button", { className: styles.nextBtn, onClick: () => handleSetPhase('example'), children: "\u041F\u0435\u0440\u0435\u0439\u0442\u0438 \u043A \u043F\u0440\u0438\u043C\u0435\u0440\u0443 \u2192" }) })] })), phase === 'example' && (_jsxs(_Fragment, { children: [_jsx(ExampleRunner, { description: lesson.example.description, sql: lesson.example.sql, onRun: handleRunExample }), _jsx("div", { className: styles.lessonNav, children: _jsx("button", { className: styles.nextBtn, onClick: () => handleSetPhase('task'), children: "\u041F\u0435\u0440\u0435\u0439\u0442\u0438 \u043A \u0437\u0430\u0434\u0430\u043D\u0438\u044E \u2192" }) })] })), phase === 'task' && (_jsxs(_Fragment, { children: [_jsx(TaskPanel, { description: lesson.task.description, hints: hints, hintsRevealed: hintsRevealed, totalHints: lesson.task.hintCount, attempts: attempts, xpReward: lesson.xpReward, onCheck: handleCheck, onRevealHint: handleRevealHint, completionResult: completionResult }), completed && (_jsx("div", { className: styles.lessonNav, children: _jsx("button", { className: styles.nextBtn, onClick: () => navigate('/'), children: "\u0412\u0435\u0440\u043D\u0443\u0442\u044C\u0441\u044F \u043A \u043A\u0430\u0440\u0442\u0435 \u2192" }) })), _jsxs("div", { className: styles.tablesRef, children: ["\uD83D\uDCCA \u0422\u0430\u0431\u043B\u0438\u0446\u044B \u0432 \u0431\u0430\u0437\u0435:", _jsx("span", { children: "products" }), "|", _jsx("span", { children: "categories" }), "|", _jsx("span", { children: "customers" }), "|", _jsx("span", { children: "orders" }), "|", _jsx("span", { children: "order_items" })] })] }))] }), _jsx(XpAnimation, { xp: xpAnimAmount, trigger: xpAnimTrigger }), _jsx(ConfettiAnimation, { trigger: confettiTrigger }), newAchievements.length > 0 && (_jsx(AchievementPopup, { achievements: newAchievements, onClose: () => setNewAchievements([]) }))] }));
}
